/**
 * CancelInvoiceDialog (Iter 64)
 *
 * Visible whenever the operator is on an invoice that meets the 4-gate
 * cancellation eligibility (server-enforced; the same checks are also
 * displayed here verbatim as a "status-check strip" so the operator
 * understands WHY the action is permitted).
 *
 * Statutory anchor: a cancellation is only valid before the invoice
 * enters GSTR-1. Once filed, the legally correct path is a Credit Note.
 *
 * Backend: POST /api/billing/invoice/{invoice_id}/cancel
 *   body: { reason_code, note }
 *
 * AMC-generated invoices auto-rewind their schedule on cancel — the
 * confirmation panel below the form explains that consequence so
 * operators know what the system will do downstream.
 */
import { useEffect, useState } from "react";
import {
    Prohibit, Warning, ShieldCheck, CheckCircle, XCircle, ArrowsCounterClockwise,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { DialogFooter } from "./ui/dialog";
import FormDialog, { FormDialogCancel } from "./FormDialog";
import { toast } from "sonner";

const CONFIRM_PHRASE = "CANCEL";

export default function CancelInvoiceDialog({ invoice, onCancelled }) {
    const [open, setOpen] = useState(false);
    const [reasons, setReasons] = useState([]);
    const [eligibility, setEligibility] = useState(null);
    const [reasonCode, setReasonCode] = useState("");
    const [note, setNote] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const [{ data: r }, { data: e }] = await Promise.all([
                    api.get("/billing/invoice/_/cancellation-reasons"),
                    api.get(`/billing/invoice/${invoice.id}/cancellation-eligibility`),
                ]);
                setReasons(r.items || []);
                setEligibility(e);
            } catch (err) {
                toast.error(formatApiError(err, "Failed to load cancellation context"));
                setOpen(false);
            }
        })();
    }, [open, invoice.id]);

    const reset = () => {
        setReasonCode("");
        setNote("");
        setConfirm("");
        setEligibility(null);
    };

    const isAmcInvoice = !!invoice.amc_schedule_id;
    const minNote = reasonCode === "other" ? 30 : 10;
    const canSubmit =
        eligibility?.eligible &&
        reasonCode &&
        note.trim().length >= minNote &&
        confirm.trim().toUpperCase() === CONFIRM_PHRASE &&
        !submitting;

    const submit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            const { data } = await api.post(
                `/billing/invoice/${invoice.id}/cancel`,
                { reason_code: reasonCode, note: note.trim() },
            );
            toast.success(
                `Invoice ${data.invoice_number || invoice.invoice_number} cancelled.`,
                { description: data.amc_rewind?.rewound
                    ? "AMC schedule auto-rewound — the period can be re-billed."
                    : "Statutorily inactive — excluded from receivables and reports." },
            );
            setOpen(false);
            reset();
            onCancelled?.();
        } catch (err) {
            toast.error(formatApiError(err, "Cancellation failed"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Button
                variant="outline"
                className="gap-1.5 border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                onClick={() => setOpen(true)}
                data-testid="cancel-invoice-trigger"
            >
                <Prohibit size={14} weight="bold" /> Cancel Invoice
            </Button>
            <FormDialog
                open={open}
                onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}
                title={
                    <span className="flex items-center gap-2">
                        <Prohibit size={20} weight="fill" className="text-rose-600" />
                        Cancel Invoice {invoice.invoice_number}
                    </span>
                }
                description={
                    <span>
                        Voids this invoice statutorily. <strong>Cannot be reversed after 24 hours.</strong>
                        {" "}Use this <em>only</em> when the invoice has not yet been filed in GSTR-1 and has zero
                        payments. For filed / paid invoices, issue a Credit Note instead.
                    </span>
                }
                testId="cancel-invoice-dialog"
            >
            <div className="space-y-4">
                {/* Status-check strip — server-driven eligibility report */}
                {eligibility && (
                    <div
                        className={`rounded-md border p-3 space-y-1.5 ${
                            eligibility.eligible
                                ? "border-emerald-200 bg-emerald-50/50"
                                : "border-rose-300 bg-rose-50/50"
                        }`}
                        data-testid="cancel-eligibility-strip"
                    >
                        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-700">
                            <ShieldCheck size={14} />
                            {eligibility.eligible
                                ? "Eligibility — all 4 statutory gates pass"
                                : "Eligibility — blocked"}
                        </div>
                        <div className="space-y-1">
                            {eligibility.checks.map((c) => (
                                <div
                                    key={c.gate}
                                    className="flex items-start gap-2 text-xs"
                                    data-testid={`cancel-gate-${c.gate}`}
                                >
                                    {c.passed ? (
                                        <CheckCircle
                                            size={14} weight="fill" className="text-emerald-600 mt-0.5 shrink-0"
                                        />
                                    ) : (
                                        <XCircle
                                            size={14} weight="fill" className="text-rose-600 mt-0.5 shrink-0"
                                        />
                                    )}
                                    <div>
                                        <div className={c.passed ? "text-slate-800" : "text-rose-900 font-semibold"}>
                                            {c.label}
                                        </div>
                                        <div className="text-[11px] text-slate-500">{c.detail}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {isAmcInvoice && eligibility?.eligible && (
                    <div
                        className="rounded-md border border-indigo-200 bg-indigo-50/40 p-3 text-xs text-indigo-900 flex items-start gap-2"
                        data-testid="cancel-amc-rewind-note"
                    >
                        <ArrowsCounterClockwise size={14} weight="bold" className="mt-0.5 shrink-0" />
                        <div>
                            <div className="font-semibold">AMC schedule will auto-rewind</div>
                            <div className="text-[11px] text-indigo-900/80 mt-0.5">
                                When this invoice is cancelled, the linked AMC schedule's next-due-date
                                will be reset to {invoice.billing_start_date?.slice(0, 10) || "the cancelled period start"}
                                {" "}and <span className="font-mono">generated_count</span> decremented by 1,
                                so the period can be re-billed via "Generate Invoice Now".
                            </div>
                        </div>
                    </div>
                )}

                <div>
                    <Label htmlFor="cancel-reason" className="text-xs uppercase tracking-wide text-slate-500">
                        Cancellation reason <span className="text-rose-600">*</span>
                    </Label>
                    <select
                        id="cancel-reason"
                        value={reasonCode}
                        onChange={(e) => setReasonCode(e.target.value)}
                        disabled={!eligibility?.eligible}
                        className="mt-1 block w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-200 disabled:bg-slate-50 disabled:text-slate-400"
                        data-testid="cancel-reason-select"
                    >
                        <option value="">Pick a reason…</option>
                        {reasons.map((r) => (
                            <option key={r.code} value={r.code}>{r.label}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <Label htmlFor="cancel-note" className="text-xs uppercase tracking-wide text-slate-500">
                        Note <span className="text-rose-600">*</span>{" "}
                        <span className="text-slate-400 normal-case">
                            (min {minNote} chars{reasonCode === "other" ? " — required when reason is Other" : ""})
                        </span>
                    </Label>
                    <Textarea
                        id="cancel-note"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={3}
                        disabled={!eligibility?.eligible}
                        placeholder={
                            reasonCode === "other"
                                ? "Explain the exact circumstance in at least 30 characters…"
                                : "Brief audit-grade note (will appear on every cancellation report)…"
                        }
                        className="mt-1 disabled:bg-slate-50"
                        data-testid="cancel-note-input"
                    />
                    <div className="text-[11px] text-slate-500 mt-0.5">
                        {note.trim().length}/{minNote} characters minimum
                    </div>
                </div>

                <div className="rounded-md border border-rose-200 bg-rose-50/50 p-3 space-y-2">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-900">
                        <Warning size={14} weight="fill" /> Final confirmation
                    </div>
                    <p className="text-[11px] text-rose-900/80 leading-relaxed">
                        Type <span className="font-mono font-bold bg-white border border-rose-200 px-1 rounded">CANCEL</span>
                        {" "}below to confirm. This action is irreversible after {" "}
                        <strong>24 hours</strong> and excludes the invoice from receivables, aging,
                        GST reports, TDS reports, forecasts, and dashboard totals.
                    </p>
                    <Input
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        placeholder="Type CANCEL"
                        disabled={!eligibility?.eligible}
                        className="font-mono text-sm disabled:bg-slate-50"
                        data-testid="cancel-confirm-input"
                    />
                </div>

                <DialogFooter>
                    <FormDialogCancel />
                    <Button
                        type="button"
                        variant="destructive"
                        disabled={!canSubmit}
                        onClick={submit}
                        className="gap-1.5"
                        data-testid="cancel-invoice-submit"
                    >
                        <Prohibit size={14} weight="bold" />
                        {submitting ? "Cancelling…" : "Cancel Invoice"}
                    </Button>
                </DialogFooter>
            </div>
        </FormDialog>
        </>
    );
}
